import { useId, useMemo, useState } from 'react';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Search } from '../../../molecules/Search/Search';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import { Table } from '../../../organisms/Table/Table';
import { Dialog } from '../../../primitives/Dialog/Dialog';
import { hashtagColumns, hashtagTableRows, type HashtagEntry } from './hashtagTable';
import { pageWindow } from './pageWindow';
import styles from './TopHashtagsModal.module.css';

/** Rows per page — the ten the frame draws, where the widget shows eight. */
const PAGE_SIZE = 10;

export type TopHashtagsModalProps = {
  open: boolean;
  onClose: () => void;
  /** The whole ranking — the same list the widget previews the top of. */
  entries: readonly HashtagEntry[];
  /**
   * The header badge — the count of tags in the whole ranking (`10 hashtags`),
   * not of the search results: it names what the modal covers.
   */
  countLabel?: string;
};

/**
 * Trends — `Top Hashtags used modal` (2211:92097), opened by `View all` on the
 * `Top hashtags used` widget.
 *
 * The widget draws the top eight and stops; this is the same ranking with
 * nothing held back. **Search and paging are client-side** over the full
 * ranking the widget's contract returns (it is ten tags deep).
 *
 * Like `Page conversations` it is a whole `Table` on the `Overlay`, so it
 * composes `Dialog` around a `Table` rather than using the `Modal` card — and
 * at the same 1192, drawn at 124,208 in the 1440x900 frame with four equal
 * 298s.
 *
 * **No `Cancel` / `Save changes`.** The frame's `Modal` instance is set to
 * `Type=Tune sentiment`, so that variant's two footer buttons came across with
 * it — there is nothing here to save, and a `Save changes` over a read-only
 * ranking is a control that would have to lie. The dialog closes on Escape and
 * on a click outside. Flagged for the designer.
 */
export function TopHashtagsModal({ open, onClose, entries, countLabel }: TopHashtagsModalProps) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  /* Matches the tag as written, so `gp` finds `#mygp`. */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === '' ? entries : entries.filter((entry) => entry.label.toLowerCase().includes(q));
  }, [entries, query]);

  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  /* Clamped on read rather than reset in an effect — `Influencers`' reason. */
  const currentPage = Math.min(page, pageCount);
  const visible = shown.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const titleId = useId();

  return (
    <Dialog open={open} onClose={onClose} labelledBy={titleId} className={styles.layer}>
      <div className={styles.panel} tabIndex={-1}>
        <Table
          label="Every hashtag used, ranked by mentions"
          /*
           * Four equal columns — the frame draws 298 each in its 1192, which is
           * the whole width split in quarters rather than a set of hugs. The
           * widget beside it is 586 and weights its four differently.
           */
          columns={hashtagColumns(['25%', '25%', '25%', '25%'])}
          rows={hashtagTableRows(visible)}
          emptyLabel={
            query.trim() === '' ? 'No tagged posts in this period' : 'No hashtags match that search'
          }
          header={
            <TableHeader
              titleId={titleId}
              title="Top hashtags used"
              badge={countLabel}
              helpText="Every tag people attached to posts about us, ranked by how many mentions carried each one."
              helpSupportingText="Only public posts can carry a tag — a direct message, a support ticket and an app-store review have no audience to be found by, so they are not counted here or in the share."
              search={
                <Search
                  aria-label="Search hashtags"
                  placeholder="Search hashtags..."
                  value={query}
                  onValueChange={(next) => {
                    setQuery(next);
                    setPage(1);
                  }}
                />
              }
            />
          }
          pagination={
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              pages={pageWindow(currentPage, pageCount)}
              onPageChange={setPage}
              label="Hashtag pages"
            />
          }
        />
      </div>
    </Dialog>
  );
}
